import { useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldValues,
  Path,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Select,
  Switch
} from '@clientbase/clientbase-library'
import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'

import ProrateItemsSelector from 'components/ProrateItemsSelector'

import { theme } from 'styles/theme'

type TypesChargesProps<T extends FieldValues> = {
  control: Control<T>
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  /**
   * Habilita a escolha de quais itens entram no rateio proporcional. Só no link de
   * contratação — no formulário de plano não existem itens para escolher.
   */
  showProrateItems?: boolean
}

const TypesCharges = <T extends FieldValues>({
  control,
  watch,
  setValue,
  showProrateItems = false
}: TypesChargesProps<T>) => {
  const { dueType, chargeType } = watch()

  // Estado para controlar qual opção está selecionada no accordion summary
  const [summaryOption, setSummaryOption] = useState<'contract_day' | 'custom'>(
    'contract_day'
  )

  // Estado para controlar as sub-opções quando "No dia da contratação" está selecionado na recorrência
  const [contractDaySubOption, setContractDaySubOption] = useState<
    'follow_frequency' | 'custom_day' | 'custom_day_same_month'
  >('follow_frequency')

  // Estado para controlar o tipo de vencimento personalizado
  const [customType, setCustomType] = useState<
    'specific_day' | 'interval_days'
  >('specific_day')

  // Gerar opções de dias específicos (1-31)
  const specificDayOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `Dia ${i + 1}`,
    value: i + 1
  }))

  // Gerar opções de intervalo de dias (Após 1 dia até Após 31 dias)
  const intervalDayOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `Após ${i + 1} dia${i > 0 ? 's' : ''}`,
    value: i + 1
  }))

  // Sincronizar estados quando estamos editando um produto existente
  useEffect(() => {
    if (dueType === 'immediate_charge') {
      setSummaryOption('contract_day')
    } else if (dueType === 'delay_charge' || dueType === 'specified_charge') {
      setSummaryOption('custom')
      if (dueType === 'delay_charge') {
        setCustomType('interval_days')
      } else {
        setCustomType('specific_day')
      }
    } else if (dueType === 'immediate_charge_and_same_month_specified_charge') {
      setSummaryOption('contract_day')
      setContractDaySubOption('custom_day_same_month')
    } else if (
      dueType === 'immediate_charge_and_specified_charge' ||
      dueType === 'delay_charge_and_specified_charge'
    ) {
      setSummaryOption('contract_day')
      setContractDaySubOption('custom_day')
    }
  }, [dueType])

  return (
    <Accordion
      expanded={
        summaryOption === 'custom' ||
        (summaryOption === 'contract_day' && chargeType === 1)
      }
    >
      <AccordionSummary
        expandIcon={<></>}
        onClick={(e) => e.stopPropagation()}
        sx={{ cursor: 'default' }}
      >
        <RadioGroup
          value={summaryOption}
          onChange={(_, value) => {
            const newSummaryOption = value as 'contract_day' | 'custom'
            setSummaryOption(newSummaryOption)

            if (newSummaryOption === 'contract_day') {
              // Para cobrança única, setar immediate_charge
              if (chargeType === 0) {
                setValue('dueType' as Path<T>, 'immediate_charge' as any)
                setValue('dueDay' as Path<T>, undefined as any)
                setValue('proratedCharge' as Path<T>, false as any)
              } else {
                // Para recorrência, setar a primeira sub-opção por padrão
                setContractDaySubOption('follow_frequency')
                setValue('dueType' as Path<T>, 'immediate_charge' as any)
                setValue('dueDay' as Path<T>, undefined as any)
                setValue('proratedCharge' as Path<T>, false as any)
              }
            } else {
              // Personalizado - setar valores padrão
              setCustomType('specific_day')
              setValue('dueType' as Path<T>, 'specified_charge' as any)
              setValue('dueDay' as Path<T>, 1 as any)
              setValue('proratedCharge' as Path<T>, false as any)
            }
          }}
          row
        >
          <FormControlLabel
            value="contract_day"
            control={<Radio />}
            label={
              <Typography
                variant="smLight"
                sx={{
                  fontWeight: summaryOption === 'contract_day' ? 600 : 400
                }}
              >
                No dia da contratação
              </Typography>
            }
          />
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label={
              <Typography
                variant="smLight"
                sx={{
                  fontWeight: summaryOption === 'custom' ? 600 : 400
                }}
              >
                Personalizado
              </Typography>
            }
          />
        </RadioGroup>
      </AccordionSummary>

      <AccordionDetails>
        {summaryOption === 'contract_day' && chargeType === 1 ? (
          // Conteúdo para "No dia da contratação" quando é recorrência
          <Stack gap={2}>
            <RadioGroup
              value={contractDaySubOption}
              onChange={(_, value) => {
                const newSubOption = value as
                  | 'follow_frequency'
                  | 'custom_day'
                  | 'custom_day_same_month'
                setContractDaySubOption(newSubOption)

                if (newSubOption === 'follow_frequency') {
                  setValue('dueType' as Path<T>, 'immediate_charge' as any)
                  setValue('dueDay' as Path<T>, undefined as any)
                  setValue('proratedCharge' as Path<T>, false as any)
                } else {
                  setValue(
                    'dueType' as Path<T>,
                    (newSubOption === 'custom_day_same_month'
                      ? 'immediate_charge_and_same_month_specified_charge'
                      : 'immediate_charge_and_specified_charge') as any
                  )
                  setValue('dueDay' as Path<T>, 1 as any)
                }
              }}
            >
              <FormControlLabel
                value="follow_frequency"
                control={<Radio />}
                label={
                  <Typography
                    variant="smLight"
                    sx={{
                      fontWeight:
                        contractDaySubOption === 'follow_frequency' ? 600 : 400
                    }}
                  >
                    Primeiro vencimento no dia da contratação e os demais seguem
                    a periodicidade escolhida (mensalmente, trimestralmente,
                    etc)
                  </Typography>
                }
              />
              <FormControlLabel
                value="custom_day"
                control={<Radio />}
                label={
                  <Typography
                    variant="smLight"
                    sx={{
                      fontWeight:
                        contractDaySubOption === 'custom_day' ? 600 : 400
                    }}
                  >
                    Primeiro vencimento no dia da contratação e os demais em dia
                    personalizado, a partir do próximo mês
                  </Typography>
                }
              />
              <FormControlLabel
                value="custom_day_same_month"
                control={<Radio />}
                label={
                  <Typography
                    variant="smLight"
                    sx={{
                      fontWeight:
                        contractDaySubOption === 'custom_day_same_month'
                          ? 600
                          : 400
                    }}
                  >
                    Primeiro vencimento no dia da contratação e os demais em dia
                    personalizado, já neste mês
                  </Typography>
                }
              />
            </RadioGroup>

            {contractDaySubOption !== 'follow_frequency' && (
              <Stack gap={1}>
                <Controller
                  control={control}
                  name={'dueDay' as Path<T>}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label="Vencimento da recorrência"
                      value={value || 1}
                      onChange={onChange}
                      fullWidth
                    >
                      {specificDayOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  mt={1}
                >
                  <Stack gap={0.5}>
                    <Typography variant="smBold">
                      Cobrar proporcional
                    </Typography>
                    <Typography color={theme.palette.neutral[400]}>
                      O valor da primeira fatura será proporcional entre o dia
                      da contratação e o dia do vencimento.
                    </Typography>
                  </Stack>
                  <Controller
                    control={control}
                    name={'proratedCharge' as Path<T>}
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        checked={value || false}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                </Box>

                <ProrateItemsSelector
                  control={control}
                  setValue={setValue}
                  show={showProrateItems}
                />
              </Stack>
            )}
          </Stack>
        ) : summaryOption === 'custom' ? (
          // Conteúdo para "Personalizado"
          <Stack gap={2}>
            <Box display="flex" width="100%" gap={2}>
              <Box flex={1}>
                <Select
                  label="Selecione uma opção"
                  value={customType}
                  onChange={(e) => {
                    const newType = e.target.value as
                      | 'specific_day'
                      | 'interval_days'
                    setCustomType(newType)

                    if (newType === 'specific_day') {
                      setValue('dueType' as Path<T>, 'specified_charge' as any)
                    } else {
                      setValue('dueType' as Path<T>, 'delay_charge' as any)
                    }
                    setValue('dueDay' as Path<T>, 1 as any)
                  }}
                  fullWidth
                >
                  <MenuItem value="specific_day">
                    Dia específico do mês
                  </MenuItem>
                  <MenuItem value="interval_days">Intervalo de dias</MenuItem>
                </Select>
              </Box>

              <Box flex={1}>
                <Controller
                  control={control}
                  name={'dueDay' as Path<T>}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label={
                        customType === 'specific_day'
                          ? 'Selecione o dia'
                          : 'Selecione o intervalo'
                      }
                      value={value || 1}
                      onChange={onChange}
                      fullWidth
                    >
                      {(customType === 'specific_day'
                        ? specificDayOptions
                        : intervalDayOptions
                      ).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
            </Box>

            {/* Switch para cobrar proporcional - só aparece para recorrência e dia específico */}
            {chargeType === 1 && customType === 'specific_day' && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Stack gap={0.5}>
                  <Typography variant="smBold">Cobrar proporcional</Typography>
                  <Typography color={theme.palette.neutral[400]}>
                    O valor da primeira fatura será proporcional entre o dia da
                    contratação e o dia do vencimento.
                  </Typography>
                </Stack>
                <Controller
                  control={control}
                  name={'proratedCharge' as Path<T>}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value || false}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </Box>
            )}

            {chargeType === 1 && customType === 'specific_day' && (
              <ProrateItemsSelector
                control={control}
                setValue={setValue}
                show={showProrateItems}
              />
            )}
          </Stack>
        ) : null}
      </AccordionDetails>
    </Accordion>
  )
}

export default TypesCharges
